import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { useEmailVerification } from "~/domains/user/hooks/useEmailVerification.ts";
import { ROUTES } from "~/router";
import AuthBanner, { BannerTypes } from "~/screens/Auth/components/AuthBanner";
import { useRecaptcha } from "~/screens/Auth/hooks/useRecaptcha";
import { AuthLayout } from "~/screens/Auth/Layout/AuthLayout";
import { Title, usePageTitle } from "~/shared";
import { safeSessionStorage } from "~/shared/utils/safeStorage";
import { useUserStore } from "~/stores";
import { errorToast } from "~/ui/admin";
import { ResendEmail } from "../components/ResendEmail";

export const EmailSent = () => {
  usePageTitle("Email sent");
  const navigate = useNavigate();
  const email = safeSessionStorage.getItem("pendingEmail");
  const {
    resend: { mutate, isPending, isSuccess },
  } = useEmailVerification();

  const hashedUserId = useUserStore((state) => state.hashedUserId);
  const hashedUserIdExpiresAt = useUserStore(
    (state) => state.hashedUserIdExpiresAt,
  );
  const { handleReCaptchaVerify } = useRecaptcha("alreadyHaveAccount");

  useEffect(() => {
    const hashedUserIdExpired =
      hashedUserIdExpiresAt && Date.now() > hashedUserIdExpiresAt;
    if (!hashedUserId || hashedUserIdExpired) {
      navigate(ROUTES.fullLogin, { replace: true });
    }
  }, [hashedUserId, hashedUserIdExpiresAt, navigate]);

  const handleResend = async () => {
    const reCaptchaToken = await handleReCaptchaVerify();
    if (hashedUserId && reCaptchaToken) {
      mutate({ hashedUserId, reCaptchaToken });
    } else {
      errorToast("We couldn't send the email.");
    }
  };

  return (
    <AuthLayout
      banner={<AuthBanner type={BannerTypes.pendingVerification} />}
      showSupport
    >
      <section className="flex w-full flex-col items-center justify-center space-y-4">
        <div className="relative w-full max-w-lg">
          <section className="space-y-6" role="alert" aria-label="information">
            <Title>We sent you an email!</Title>
            <p className="text-indigo mb-3 break-words font-sans">
              We sent an email{email ? ` to ${email}` : ""}. Please check your
              inbox to verify your account and complete your registration.
            </p>
          </section>

          <ResendEmail
            isSuccess={isSuccess}
            isPending={isPending}
            onClick={handleResend}
          />
        </div>
      </section>
    </AuthLayout>
  );
};
